import { BillingCycle } from '@/firebase/auth/auth-jwt-schema'
import { RootState } from '@/store/store'
import { useAppSelector } from '@/store/store-hooks'
import { selectAmountDue, selectMonthlyPlanAnnualCost } from '../store/selectors'
import { formatCentsToDollarsCompact } from '@/util/money'

const millisecondsPerDay = 24 * 60 * 60 * 1000
const currentDate = new Date()

export enum CheckoutType {
  SIGNUP = 'signup',
  RESIGNUP = 'resignup',
}

interface DueSectionProps {
  checkoutType: CheckoutType
}

export function DueSection(props: DueSectionProps) {
  const { checkoutType } = props

  // Redux state
  const displayTrialDays = useAppSelector((state: RootState) => state.checkoutState.displayTrialDays)
  const actualTrialDays = useAppSelector((state: RootState) => state.checkoutState.actualTrialDays)

  const billingCycle = useAppSelector((state: RootState) => state.checkoutState.billingCycle)
  const promotion = useAppSelector((state: RootState) => state.checkoutState.promotion)
  const amountDue = useAppSelector((state: RootState) => selectAmountDue(state))
  const monthlyPlanAnnualCost = useAppSelector((state: RootState) => selectMonthlyPlanAnnualCost(state))

  // Calculated values
  const dueDate = new Date(currentDate.getTime() + actualTrialDays * millisecondsPerDay)
  const dueDateString = dueDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })

  return (
    <div className="space-y-2 mb-3 bg-brand-neutral-50 rounded-lg">
      {/* Show $0 due today for signup (they get a free trial) */}
      {checkoutType === CheckoutType.SIGNUP && (
        <div className="flex justify-between items-center">
          <span className="text-brand-neutral-700">
            <span className="font-bold">Due today</span>
            {'  '}
            <span className="text-brand-neutral-600">({displayTrialDays} days free)</span>
          </span>
          <span className="text-brand-neutral-600">$0</span>
        </div>
      )}

      {/* Section with actual amount due - today for resignup, end of trial for signup */}
      <div className="flex justify-between items-center">
        <span className="text-brand-neutral-700 font-semibold">Due {checkoutType === CheckoutType.SIGNUP ? dueDateString : 'today'}</span>
        <div className="flex items-center">
          {billingCycle === BillingCycle.ANNUAL && (
            <span className="text-brand-neutral-500 line-through mr-2">{formatCentsToDollarsCompact(monthlyPlanAnnualCost)}</span>
          )}
          <span className="text-brand-neutral-600">{formatCentsToDollarsCompact(amountDue)}</span>
        </div>{' '}
      </div>

      {/* Promotion description */}
      {promotion && (
        <div className="flex justify-between items-center gap-4">
          <span className="text-green-700 font-semibold">{promotion.code}</span>
          <span className="text-green-700">{promotion.coupon.name}</span>
        </div>
      )}
    </div>
  )
}

export default DueSection
